import I18n from 'components/I18n'
import { OverlayLoader } from 'components/Loaders'
import { actions, selectors } from 'data'
import messages from 'lang'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import CryptoOptions from '../Crypto/CryptoOptions'
import EligibilityNotifications from '../EligibilityNotifications'
import FiatModal from '../Fiat/FiatModal'
import FiatOptions from '../Fiat/FiatOptions'
import {
  Header,
  ModalCard,
  ModalTitle,
  StyledDarkTabMenuItem,
  StyledRoundedTabMenu,
  StyledSeparator
} from '../styles'
import WithdrawCryptoFormModal from './Crypto/WithdrawCryptoFormModal'
import getCryptoWithdrawOptions from './getCryptoWithdrawOptions'
import getFiatWithdrawOptions from './getFiatWithdrawOptions'

type Props = { currency?: string }
const WithdrawModal = ({ currency: passedCurrency }: Props) => {
  const [currency, setCurrency] = useState<string | undefined>(passedCurrency)
  const [tab, setTab] = useState<'CASH' | 'CRYPTO'>('CASH')

  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(actions.account.fetchBalances())
  }, [])

  const assetCurrencies = useSelector(selectors.currencies.getCurrencies)
  const instrumentCurrencies = useSelector(selectors.instrument.getRawCurrencies)
  const paymentCurrencies = useSelector(selectors.payments.currencies.getCurrencies)
  const balances = useSelector(selectors.account.getBalances)
  const fiats = getFiatWithdrawOptions({
    assetCurrencies,
    balances,
    instrumentCurrencies,
    paymentCurrencies
  })
  const cryptos = getCryptoWithdrawOptions({
    assetCurrencies,
    balances,
    instrumentCurrencies,
    paymentCurrencies
  })
  const eligibility = useSelector(selectors.eligibility.getEligibility)

  if (!balances)
    return (
      <ModalCard>
        <OverlayLoader />
      </ModalCard>
    )

  if (!currency) {
    return (
      <ModalCard>
        <Header>
          <ModalTitle>
            <I18n data={messages.withdraw_title} />
          </ModalTitle>
        </Header>
        <StyledRoundedTabMenu>
          <StyledDarkTabMenuItem onClick={() => setTab('CASH')} selected={tab === 'CASH'}>
            <I18n data={messages.positions_cash} />
          </StyledDarkTabMenuItem>
          <StyledDarkTabMenuItem onClick={() => setTab('CRYPTO')} selected={tab === 'CRYPTO'}>
            <I18n data={messages.crypto} />
          </StyledDarkTabMenuItem>
        </StyledRoundedTabMenu>
        {tab === 'CASH' && <StyledSeparator />}
        {tab === 'CASH' &&
          (eligibility.withdrawFiat.enabled ? (
            <FiatOptions
              currencies={fiats}
              onItemClick={(currency) => {
                setCurrency(currency)
                dispatch(
                  actions.analytics.trackEvent({
                    key: 'Exchange Withdraw Currency Selected',
                    properties: {
                      currency,
                      type: 'FIAT'
                    }
                  })
                )
              }}
            />
          ) : (
            <EligibilityNotifications
              context='WITHDRAW'
              reason={eligibility.withdrawFiat.reasonNotEligible}
            />
          ))}
        {tab === 'CRYPTO' &&
          (eligibility.withdrawCrypto.enabled ? (
            <CryptoOptions
              currencies={cryptos}
              onItemClick={(currency, query) => {
                setCurrency(currency)
                dispatch(
                  actions.analytics.trackEvent({
                    key: 'Exchange Withdraw Currency Selected',
                    properties: {
                      currency,
                      search: query,
                      type: 'CRYPTO'
                    }
                  })
                )
              }}
            />
          ) : (
            <EligibilityNotifications
              context='WITHDRAW'
              reason={eligibility.withdrawCrypto.reasonNotEligible}
            />
          ))}
      </ModalCard>
    )
  }

  if (fiats.some((f) => f.symbol === currency)) {
    return <FiatModal currency={currency} flow='WITHDRAW' />
  }

  return <WithdrawCryptoFormModal currency={currency} />
}

export default WithdrawModal
